import { useRef, useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Button, Paper, Box, Typography } from '@mui/material';
import { Link } from 'react-router-dom';
import * as XLSX from 'xlsx-js-style';
import { formatDate } from '../../common/constants';
import { getStoredCompanyDetails, getCompanyDisplayFromDetails, getShortCompanyName } from '../../services/companyService';

function PFReport() {
    const location = useLocation();
    const companyDetails = useSelector((state) => state.auth.activeCompanyDetails) || getStoredCompanyDetails();
    const { companyName, addressLine1, addressLine2 } = getCompanyDisplayFromDetails(companyDetails || {});
    const PFTableRef = useRef(null);
    const [forceUpdate, setForceUpdate] = useState(0);
    // const [empDetails, setEmpDetails] = useState();
    // const [pfDetail, setPfDetail] = useState();
    const {empDetails, pfDetail, month, year} = location.state;
    // console.log(location.state);
    // console.log(empDetails);

    // Helper function to format numbers with comma separation
    const formatNumber = (num) => {
        return Math.round(num).toLocaleString('en-IN');
    };

    // Helper function to clean UAN/PF numbers for display (removes all quotes)
    const cleanDisplayValue = (value) => {
        if (value === null || value === undefined || value === '') return '';
        // Convert to string, remove ALL single quotes (anywhere in the string), and trim whitespace
        let cleaned = String(value);
        // Remove all single quotes
        cleaned = cleaned.replace(/'/g, '');
        // Remove any other quote-like characters
        cleaned = cleaned.replace(/[''""]/g, '');
        cleaned = cleaned.trim();
        return cleaned;
    };

    // Payroll saved with the day-wise PF rule split stores its PF/EPS/EDLI wages and amounts; use those.
    // Older payroll rows (pf_wages empty) are recalculated from Gross with the month-end PF rule.
    function getPfFigures(empDetail) {
        if (empDetail.pf_wages !== null && empDetail.pf_wages !== undefined) {
            return {
                Basic_for_PF: Math.round(Number(empDetail.pf_wages) || 0),
                EPF: Math.round(Number(empDetail.provident_fund) || 0),
                EPS: Math.round(Number(empDetail.eps_amount) || 0),
                EDLI: Math.round(Number(empDetail.edli_amount) || 0),
                EPS_Wages: Math.round(Number(empDetail.eps_wages) || 0),
                EDLI_Wages: Math.round(Number(empDetail.edli_wages) || 0),
            };
        }
        const gross = empDetail.Gross;
        const pfLimit = pfDetail.PfBasicLimit;
        const pfPercent = pfDetail.PfBasicPer / 100;
        const epsLimit = pfDetail.epsSalaryLimit;
        const epsPercent = pfDetail.epsPercent / 100;
        const edliLimit = pfDetail.edliSalaryLimit;
        const edliPercent = pfDetail.edliPercent / 100;
        return {
            Basic_for_PF: Math.round(gross >= pfLimit ? pfLimit : gross),
            EPF: Math.round(gross >= pfLimit ? pfLimit * pfPercent : gross * pfPercent),
            EPS: Math.round(gross >= epsLimit ? epsLimit * epsPercent : gross * epsPercent),
            EDLI: Math.round(gross >= edliLimit ? edliLimit * edliPercent : gross * edliPercent),
            EPS_Wages: Math.round(gross >= epsLimit ? epsLimit : gross),
            EDLI_Wages: Math.round(gross >= edliLimit ? edliLimit : gross),
        };
    }

    // Months where the PF rule changed mid-month get extra EPF columns for each rule period.
    const splitRules = (pfDetail?.rulesApplied?.length || 0) > 1 ? pfDetail.rulesApplied : [];

    const countDays = (from, to) => Math.round((new Date(to) - new Date(from)) / 86400000) + 1;

    const getSplitLabel = (index) => {
        if (splitRules.length === 2) return index === 0 ? 'Before Ceiling' : 'After Ceiling';
        return `Rule ${index + 1}`;
    };

    const splitColumnHeaders = splitRules.flatMap((rule, index) => {
        const label = getSplitLabel(index);
        return [
            `Gross Salary ${label}`,
            `EPF Salary Cap ${label}`,
            `EPF Salary ${label} Proportionate (${countDays(rule.AppliesFrom, rule.AppliesTo)} days)`,
            `EPF ${label}`,
        ];
    });

    const parseBreakdown = (value) => {
        if (Array.isArray(value)) return value;
        try {
            const parsed = JSON.parse(value || '[]');
            return Array.isArray(parsed) ? parsed : [];
        } catch {
            return [];
        }
    };

    function getSplitFigures(empDetail) {
        const parts = parseBreakdown(empDetail.pf_rule_breakdown);
        const monthDays = splitRules.reduce((sum, rule) => sum + countDays(rule.AppliesFrom, rule.AppliesTo), 0) || 1;
        const gross = Number(empDetail.Gross) || 0;
        return splitRules.map((rule) => {
            const part = parts.find((p) => String(p.rule_effective_from).slice(0, 10) === String(rule.EffectiveFrom).slice(0, 10));
            const periodDays = countDays(rule.AppliesFrom, rule.AppliesTo);
            const grossShare = Math.round((gross * periodDays) / monthDays);
            const limit = Number(part?.pf_limit ?? rule.PfBasicLimit) || 0;
            return {
                grossShare,
                // Cap column = lower of period Gross and the rule limit (matches the sheet)
                cap: Math.round(Math.min(grossShare, limit)),
                wages: part ? Math.round(Number(part.pf_wages) || 0) : 0,
                epf: part ? Math.round(Number(part.pf) || 0) : 0,
            };
        });
    }

    const pffilteredEmployees = empDetails.filter(item => item.pf_applicable === 1);
    const empFilteredDetails = pffilteredEmployees.map((empDetail, index)=>{
        const rawName = (empDetail?.full_name && String(empDetail.full_name).trim()) || `${empDetail.first_name || ''} ${empDetail.last_name || ''}`.trim();
        return({
            Sr_No : index,
            Employee_Code : empDetail.employee_code,
            Employee_Name : rawName.toUpperCase(),
            Total_no_of_days : empDetail.total_days,
            NCP_days : Number(empDetail.total_days - empDetail.days_worked),
            UAN_Number : empDetail.UANNumber ? String(empDetail.UANNumber).replace(/'/g, '').trim() : '',
            PF_number : empDetail.pf_number ? String(empDetail.pf_number).replace(/'/g, '').trim() : '',
            Basic : Math.round(empDetail.Basic),
            Gross : Math.round(empDetail.Gross),
            // Stored payroll VPF ₹ (0 if not applicable / not calculated)
            VPF : Math.round(Number(empDetail.VPFAmount) || 0),
            PF : getPfFigures({ ...empDetail, Gross: Math.round(empDetail.Gross) }),
            Split : getSplitFigures(empDetail),
        })
    });

    const totalEmployees = empDetails.length;
    let totalExcludedEmployees = 0;
    let totalExcludedEmployeesGross = 0;
    let totalExcludedEmployeesBasic = 0;
    empDetails.forEach(empDetail => {
        if(!empDetail.pf_applicable) {
            totalExcludedEmployees += 1;
            totalExcludedEmployeesGross += Math.round(empDetail.Gross);
            totalExcludedEmployeesBasic += Math.round(empDetail.Basic);
        }
    });

    // Effect to ensure UAN/PF cells are always clean (no quotes) in the UI
    useEffect(() => {
        const cleanCells = () => {
            if (!PFTableRef.current) return;
            const uanCells = PFTableRef.current.querySelectorAll('td[data-uan="true"], td[data-pf="true"]');
            uanCells.forEach((cell) => {
                let cellText = cell.textContent || cell.innerText || '';
                // Remove ALL quotes and quote-like characters
                const cleanValue = cellText.replace(/'/g, '').replace(/[''""]/g, '').trim();
                if (cellText !== cleanValue) {
                    cell.textContent = cleanValue;
                    cell.innerHTML = cleanValue;
                }
            });
        };
        
        // Clean immediately and multiple times to catch any delayed updates
        cleanCells();
        const timer1 = setTimeout(cleanCells, 50);
        const timer2 = setTimeout(cleanCells, 100);
        const timer3 = setTimeout(cleanCells, 300);
        const timer4 = setTimeout(cleanCells, 500);
        return () => {
            clearTimeout(timer1);
            clearTimeout(timer2);
            clearTimeout(timer3);
            clearTimeout(timer4);
        };
    }, [empFilteredDetails, forceUpdate]);

    // Generate and download Excel file using xlsx-js-style
    const handleExcelDownload = () => {
        const wb = XLSX.utils.book_new();
        const wsData = [];

        const headerNames = ['Sr No', 'Employee Code', 'Employee Name', 'Total no of days', 'NCP days', 'UAN Number', 'PF number', 'Basic (INR)', 'Basic for PF (INR)', 'Gross (INR)', ...splitColumnHeaders.map((name) => `${name} (INR)`), 'EPF (INR)', 'VPF (INR)', 'EPS (INR)', 'EPF-EPS (INR)', 'Total (INR)', 'EDLI (INR)'];
        let total_Basic = 0, total_Basic_for_PF = 0, total_Gross = 0, total_EPF = 0, total_VPF = 0, total_EPS = 0, total_EPF_EPS = 0, total_Total = 0, total_EDLI = 0, total_NCP_days = 0;
        const excelSplitTotals = splitRules.map(() => ({ grossShare: 0, wages: 0, epf: 0 }));
        const fullDataRows = [];

        empFilteredDetails.forEach((curElem) => {
            const { Basic_for_PF, EPF, EPS, EDLI } = curElem.PF;
            const VPF = Math.round(Number(curElem.VPF) || 0);
            const EPF_EPS = EPF - EPS;
            const Total = EPS + EPF_EPS;

            total_Basic += curElem.Basic;
            total_Basic_for_PF += Basic_for_PF;
            total_Gross += curElem.Gross;
            total_EPF += EPF;
            total_VPF += VPF;
            total_EPS += EPS;
            total_EPF_EPS += EPF_EPS;
            total_Total += Total;
            total_EDLI += EDLI;
            total_NCP_days += curElem.NCP_days;
            curElem.Split.forEach((part, index) => {
                excelSplitTotals[index].grossShare += part.grossShare;
                excelSplitTotals[index].wages += part.wages;
                excelSplitTotals[index].epf += part.epf;
            });

            fullDataRows.push([
                curElem.Sr_No + 1,
                curElem.Employee_Code,
                curElem.Employee_Name,
                curElem.Total_no_of_days,
                curElem.NCP_days,
                cleanDisplayValue(curElem.UAN_Number),
                cleanDisplayValue(curElem.PF_number),
                curElem.Basic,
                Basic_for_PF,
                curElem.Gross,
                ...curElem.Split.flatMap((part) => [part.grossShare, part.cap, part.wages, part.epf]),
                EPF,
                VPF,
                EPS,
                EPF_EPS,
                Total,
                EDLI
            ]);
        });

        const isZeroOrNull = (v) => v == null || v === '' || (typeof v === 'number' && !isNaN(v) && Number(v) === 0);
        const visibleColIndices = [0, 1, 2];
        for (let c = 3; c < headerNames.length; c++) {
            const hasNonZero = fullDataRows.some(row => !isZeroOrNull(row[c]));
            if (hasNonZero) visibleColIndices.push(c);
        }
        const numCols = visibleColIndices.length;
        const pick = (row) => visibleColIndices.map(c => row[c]);

        // Company header rows from company details table
        wsData.push([companyName || '', ...Array(numCols - 1).fill('')]);
        wsData.push([addressLine1 || '', ...Array(numCols - 1).fill('')]);
        wsData.push([addressLine2 || '', ...Array(numCols - 1).fill('')]);
        wsData.push([`Provident Fund details for ${month} ${year}`, ...Array(numCols - 1).fill('')]);

        wsData.push(pick(headerNames));
        fullDataRows.forEach(row => wsData.push(pick(row)));

        const totalRow = ['', '', 'Total', '', Math.round(total_NCP_days), '', '', total_Basic, total_Basic_for_PF, total_Gross, ...excelSplitTotals.flatMap((t) => [t.grossShare, '', t.wages, t.epf]), total_EPF, total_VPF, total_EPS, total_EPF_EPS, total_Total, total_EDLI];
        wsData.push(pick(totalRow));

        wsData.push(Array(numCols).fill(''));
        const summarySectionStartRow = wsData.length;
        wsData.push(['Total number of employees', '', totalEmployees, ...Array(numCols - 3).fill('')]);
        wsData.push(['Total Subscibers', '', empFilteredDetails.length, ...Array(numCols - 3).fill('')]);
        wsData.push(['Total number of employees excluded', '', totalExcludedEmployees, ...Array(numCols - 3).fill('')]);
        wsData.push(['Gross salary of excluded emp', '', totalExcludedEmployeesGross, ...Array(numCols - 3).fill('')]);
        wsData.push(['Gross salary of excluded emp (Basic)', '', totalExcludedEmployeesBasic, ...Array(numCols - 3).fill('')]);
        wsData.push(['Total NCP days', '', Math.round(total_NCP_days), ...Array(numCols - 3).fill('')]);
        wsData.push(Array(numCols).fill(''));
        wsData.push(['Summary', '', 'INR', ...Array(numCols - 3).fill('')]);
        wsData.push(['EPF Wages', '', total_Basic_for_PF, ...Array(numCols - 3).fill('')]);
        wsData.push(['EPF Employee', '', total_EPF, ...Array(numCols - 3).fill('')]);
        wsData.push(['VPF Employee', '', total_VPF, ...Array(numCols - 3).fill('')]);
        wsData.push(['EPF-EPS', '', total_EPF_EPS, ...Array(numCols - 3).fill('')]);
        wsData.push(['EPS', '', total_EPS, ...Array(numCols - 3).fill('')]);
        wsData.push(['EPF Admin', '', Math.round(total_Basic_for_PF * epfAdminPercent <= epfAdminMinAmt ? epfAdminMinAmt : total_Basic_for_PF * epfAdminPercent), ...Array(numCols - 3).fill('')]);
        wsData.push(['EDLI', '', total_EDLI, ...Array(numCols - 3).fill('')]);
        wsData.push(['EPF Insp', '', Math.round(total_Basic_for_PF * epfInspectionPercent <= epfInspectionMinAmt ? epfInspectionMinAmt : total_Basic_for_PF * epfInspectionPercent), ...Array(numCols - 3).fill('')]);
        wsData.push(['EDLI Admin', '', 0, ...Array(numCols - 3).fill('')]);
        wsData.push(['EDLI Insp', '', Math.round(total_Basic_for_PF * edliInspectionPercent <= edliInspectionMinAmt ? edliInspectionMinAmt : total_Basic_for_PF * edliInspectionPercent), ...Array(numCols - 3).fill('')]);
        wsData.push(['Total Challan', '', total_EPF + total_VPF + total_EPF_EPS + total_EPS + Math.round(total_Basic_for_PF * epfAdminPercent <= epfAdminMinAmt ? epfAdminMinAmt : total_Basic_for_PF * epfAdminPercent) + total_EDLI + Math.round(total_Basic_for_PF * epfInspectionPercent <= epfInspectionMinAmt ? epfInspectionMinAmt : total_Basic_for_PF * epfInspectionPercent) + 0 + Math.round(total_Basic_for_PF * edliInspectionPercent <= edliInspectionMinAmt ? edliInspectionMinAmt : total_Basic_for_PF * edliInspectionPercent), ...Array(numCols - 3).fill('')]);
        const summarySectionEndRow = wsData.length - 1;
        const summarySpacerRow = summarySectionStartRow - 1;
        const accountSpacerRow = wsData.length;

        // Account Wise Bifurcation section (same content as UI)
        // A/C 1 = statutory EE EPF + VPF (EE voluntary) + ER EPF-EPS
        const totalEpfContributionAc1 = total_EPF + total_VPF + total_EPF_EPS;
        const epfAdminCharge = Math.round(total_Basic_for_PF * epfAdminPercent <= epfAdminMinAmt ? epfAdminMinAmt : total_Basic_for_PF * epfAdminPercent);
        const epfInspectionCharge = Math.round(total_Basic_for_PF * epfInspectionPercent <= epfInspectionMinAmt ? epfInspectionMinAmt : total_Basic_for_PF * epfInspectionPercent);
        const edliInspectionCharge = Math.round(total_Basic_for_PF * edliInspectionPercent <= edliInspectionMinAmt ? edliInspectionMinAmt : total_Basic_for_PF * edliInspectionPercent);
        const totalAmount = total_EPF + total_VPF + total_EPF_EPS + total_EPS + epfAdminCharge + total_EDLI + epfInspectionCharge + 0 + edliInspectionCharge;

        const accountSectionStartRow = wsData.length;
        wsData.push(Array(numCols).fill(''));
        wsData.push(['Account Wise Bifurcation of Balance Amount', ...Array(numCols - 1).fill('')]);
        wsData.push(['Total EPF Contribution (A/C 1)', 'Total EPF Contribution EE Share (incl. VPF) + Total EPF Contribution ER Share + Total Refund of Advance (₹)', totalEpfContributionAc1, ...Array(numCols - 3).fill('')]);
        wsData.push(['Total EPF Charges (A/C 2)', 'Administration Charge (₹)', epfAdminCharge, ...Array(numCols - 3).fill('')]);
        wsData.push(['Total EPF Charges (A/C 2)', 'Inspection Charge (₹)', 'Not Applicable', ...Array(numCols - 3).fill('')]);
        wsData.push(['Total EPS Contribution (A/C 10) (₹)', '', total_EPS, ...Array(numCols - 3).fill('')]);
        wsData.push(['Total EDLI Contribution (ER Share A/C 21) (₹)', '', total_EDLI, ...Array(numCols - 3).fill('')]);
        wsData.push(['Total EDLI Charges (A/C 22) (₹)', 'Administration Charges (₹)', 0, ...Array(numCols - 3).fill('')]);
        wsData.push(['Total EDLI Charges (A/C 22) (₹)', 'Inspection Charge (₹)', 'Not Applicable', ...Array(numCols - 3).fill('')]);
        wsData.push(['Total Amount (₹)', '', totalAmount, ...Array(numCols - 3).fill('')]);
        const accountHeadingRow = accountSectionStartRow + 1;
        const accountSectionEndRow = wsData.length - 1;

        const ws = XLSX.utils.aoa_to_sheet(wsData);

        const calculateColumnWidths = (data) => {
            const colWidths = [];
            const dataNumCols = data[0]?.length || numCols;

            for (let col = 0; col < dataNumCols; col++) {
                let maxWidth = 0;

                data.forEach((row, rowIndex) => {
                    // Skip header rows (rows 0-4)
                    if (rowIndex < 4) return;

                    if (row[col] !== undefined && row[col] !== null) {
                        const cellValue = String(row[col]);
                        maxWidth = Math.max(maxWidth, cellValue.length);
                    }
                });

                let wch = Math.max(maxWidth + 1, 8);
                // Keep sheet compact (avoid very wide layout)
                if (col === 0) wch = Math.min(wch, 30); // labels
                else if (col === 1) wch = Math.min(wch, 36); // account-wise description
                else if (col === 2) wch = Math.min(wch, 14); // values
                else wch = Math.min(wch, 12); // remaining columns
                colWidths.push({ wch });
            }

            return colWidths;
        };

        ws['!cols'] = calculateColumnWidths(wsData);

        ws['!merges'] = [
            { s: { r: 0, c: 0 }, e: { r: 0, c: numCols - 1 } },
            { s: { r: 1, c: 0 }, e: { r: 1, c: numCols - 1 } },
            { s: { r: 2, c: 0 }, e: { r: 2, c: numCols - 1 } },
            { s: { r: 3, c: 0 }, e: { r: 3, c: numCols - 1 } },
            { s: { r: accountHeadingRow, c: 0 }, e: { r: accountHeadingRow, c: 2 } }
        ];

        // Common border style
        const borderStyle = {
            top: { style: "thin", color: { rgb: "000000" } },
            bottom: { style: "thin", color: { rgb: "000000" } },
            left: { style: "thin", color: { rgb: "000000" } },
            right: { style: "thin", color: { rgb: "000000" } }
        };

        // Style for company name header (no left border to avoid lines on left)
        const companyHeaderBorder = {
            top: { style: "thin", color: { rgb: "000000" } },
            bottom: { style: "thin", color: { rgb: "000000" } },
            right: { style: "thin", color: { rgb: "000000" } }
        };
        const companyNameStyle = {
            font: { bold: true, sz: 16, color: { rgb: "000000" } },
            alignment: { horizontal: "center", vertical: "center" },
            border: companyHeaderBorder
        };

        // Style for column headers (lighter blue)
        const headerStyle = {
            font: { bold: true, color: { rgb: "FFFFFF" } },
            fill: { fgColor: { rgb: "5B9BD5" } },
            alignment: { horizontal: "center", vertical: "center" },
            border: borderStyle
        };

        // Style for data cells
        const dataStyle = {
            border: borderStyle,
            alignment: { vertical: "center" }
        };

        // Apply style to company header rows (rows 1-4)
        for (let row = 0; row <= 3; row++) {
            const cellAddress = XLSX.utils.encode_cell({ r: row, c: 0 });
            if (ws[cellAddress]) {
                ws[cellAddress].s = companyNameStyle;
            }
            
            // Remove empty cells in merged range (to prevent visible lines)
            for (let col = 1; col < numCols; col++) {
                const emptyCellAddress = XLSX.utils.encode_cell({ r: row, c: col });
                if (ws[emptyCellAddress]) {
                    delete ws[emptyCellAddress];
                }
            }
        }

        for (let col = 0; col < numCols; col++) {
            const cellAddress = XLSX.utils.encode_cell({ r: 4, c: col });
            if (ws[cellAddress]) {
                ws[cellAddress].s = headerStyle;
            }
        }

        // Apply styles to data cells
        const range = XLSX.utils.decode_range(ws['!ref']);
        for (let row = 5; row <= range.e.r; row++) {
            for (let col = 0; col < numCols; col++) {
                const cellAddress = XLSX.utils.encode_cell({ r: row, c: col });
                if (ws[cellAddress]) {
                    const isMainTotal = row === 5 + empFilteredDetails.length;
                    const isSummaryLabel = col === 0 && row > 5 + empFilteredDetails.length + 1;
                    const isAccountRow = row >= accountHeadingRow && row <= accountSectionEndRow;
                    const isAccountHeading = row === accountHeadingRow;
                    const isAccountTotalAmount = row === accountSectionEndRow;
                    const cellValue = wsData[row]?.[col];
                    const isNumericCell = typeof cellValue === 'number' && !Number.isNaN(cellValue);
                    ws[cellAddress].s = {
                        ...dataStyle,
                        font: { bold: isMainTotal || isSummaryLabel || isAccountHeading || isAccountTotalAmount },
                        alignment: {
                            ...dataStyle.alignment,
                            horizontal: isAccountRow
                                ? (col === 2 ? "right" : "left")
                                : (isNumericCell ? "right" : (col >= 3 && col < numCols ? "right" : "left")),
                            wrapText: isSummaryLabel || (isAccountRow && (col === 0 || col === 1))
                        }
                    };
                    if (isNumericCell) ws[cellAddress].z = '#,##0';
                }
            }
        }

        // Keep summary and account-wise sections as compact 3-column blocks like UI
        // Also clear spacer rows to avoid right-side empty bordered blocks.
        [summarySpacerRow, accountSpacerRow].forEach((row) => {
            for (let col = 3; col < numCols; col++) {
                const extraCellAddress = XLSX.utils.encode_cell({ r: row, c: col });
                if (ws[extraCellAddress]) {
                    delete ws[extraCellAddress];
                }
            }
        });

        for (let row = summarySectionStartRow; row <= summarySectionEndRow; row++) {
            for (let col = 3; col < numCols; col++) {
                const extraCellAddress = XLSX.utils.encode_cell({ r: row, c: col });
                if (ws[extraCellAddress]) {
                    delete ws[extraCellAddress];
                }
            }
        }

        for (let row = accountHeadingRow; row <= accountSectionEndRow; row++) {
            for (let col = 3; col < numCols; col++) {
                const extraCellAddress = XLSX.utils.encode_cell({ r: row, c: col });
                if (ws[extraCellAddress]) {
                    delete ws[extraCellAddress];
                }
            }
        }

        XLSX.utils.book_append_sheet(wb, ws, 'Provident Fund');
        XLSX.writeFile(wb, `${getShortCompanyName(companyName) || 'Report'} Report for Provident Fund ${month.substring(0, 3)} ${year}.xlsx`);
    };

    let total_Basic = 0,
    total_Basic_for_PF = 0,
    total_Gross = 0,
    total_EPF = 0,
    total_VPF = 0,
    total_EPS = 0,
    total_EPF_EPS = 0,
    total_Total = 0,
    total_EDLI = 0,
    total_NCP_days = 0;
    const splitTotals = splitRules.map(() => ({ grossShare: 0, wages: 0, epf: 0 }));
    const extraCols = splitColumnHeaders.length;
    const splitCellStyle = {textAlign: 'right', padding: '12px 8px', borderBottom: '1px solid #dee2e6', backgroundColor: '#fff8e1'};

    /** Company Specific Information */
    const epfAdminMinAmt=pfDetail.epfAdminMinAmt;
    const epfAdminPercent=pfDetail.epfAdminPercent/100;

    const epfInspectionMinAmt=pfDetail.epfInspectionMinAmt;
    const epfInspectionPercent=pfDetail.epfInspectionPercent/100;

    const edliAdminMinAmt=pfDetail.edliAdminMinAmt;
    const edliAdminPercent=pfDetail.edliAdminPercent/100;
    
    const edliInspectionMinAmt=pfDetail.edliInspectionMinAmt;
    const edliInspectionPercent=pfDetail.edliInspectionPercent/100;


    let txtData = "";


     const ecrPfApplicableEmployees = empDetails.filter(item => item?.pf_applicable === 1);

     const ecrEmpFilteredDetails = ecrPfApplicableEmployees.map((empDetail, index)=>{
        const pf = getPfFigures(empDetail);
        const statutoryEpf = pf.EPF;
        const statutoryEps = pf.EPS;
        // VPF is EE-only voluntary contribution; remitted with EE share (field 7)
        const vpfAmount = Math.round(Number(empDetail.VPFAmount) || 0);
        return({
            UAN : empDetail.UANNumber,
            MEMBER_NAME : String((empDetail?.full_name && String(empDetail.full_name).trim()) || `${empDetail?.first_name || ''} ${empDetail?.last_name || ''}`.trim() || '').toUpperCase(),
            GROSS_WAGES : Math.round(empDetail.Gross),
            EPF_WAGES : pf.Basic_for_PF,
            EPS_WAGES : pf.EPS_Wages,
            EDLI_WAGES : pf.EDLI_Wages,
            EPF_CONTRI_REMITTED : statutoryEpf + vpfAmount,
            EPS_CONTRI_REMITTED : statutoryEps,
            // ER share remains statutory EPF − EPS only (VPF has no employer match)
            EPF_EPS_DIFF_REMITTED : statutoryEpf - statutoryEps,
            NCP_DAYS : Number(empDetail.total_days - empDetail.days_worked),
            REFUND_OF_ADVANCES : '0',
        })
    });



    txtData += ecrEmpFilteredDetails.map( (curElem)=>{
        return (
            (curElem.UAN?curElem.UAN:0) + "#~#" 
             + (curElem.MEMBER_NAME?curElem.MEMBER_NAME:0) + "#~#" 
             + (curElem.GROSS_WAGES?curElem.GROSS_WAGES:0) + "#~#" 
             + (curElem.EPF_WAGES?curElem.EPF_WAGES:0) + "#~#" 
             + (curElem.EPS_WAGES?curElem.EPS_WAGES:0) + "#~#"
             + (curElem.EDLI_WAGES?curElem.EDLI_WAGES:0) + "#~#" 
             + (curElem.EPF_CONTRI_REMITTED?curElem.EPF_CONTRI_REMITTED:0) + "#~#" 
             + (curElem.EPS_CONTRI_REMITTED?curElem.EPS_CONTRI_REMITTED:0) + "#~#" 
             + (curElem.EPF_EPS_DIFF_REMITTED?curElem.EPF_EPS_DIFF_REMITTED:0) + "#~#" 
             + (curElem.NCP_DAYS?curElem.NCP_DAYS:0) + "#~#" 
             + (curElem.REFUND_OF_ADVANCES?curElem.REFUND_OF_ADVANCES:0) + "\n" 
        )
    })
    // console.log(txtData);
    while(txtData.indexOf(',') != -1) {
        txtData = txtData.replace(',','');
    }

    let downloadTxtFile = () => {
        const element = document.createElement("a");
        const file = new Blob([ txtData ], {type: 'text/plain;charset=utf-8'});
        element.href = URL.createObjectURL(file);
        element.download = `${getShortCompanyName(companyName) || 'Report'}ReportforECR${month.substring(0, 3)}${year}.txt`;
        // document.body.appendChild(element);
        element.click();
    }

    return (
        <Box sx={{display: "flex", justifyContent: "center", padding: "10px", overflowX: "hidden", maxWidth: "100vw"}}>
            <Box sx={{ width: "100%", maxWidth: "100%", padding: '20px', minWidth: 0, overflow: "hidden" }}>
            <Button
                variant='contained'
                color='success'
                onClick={ handleExcelDownload }
                sx={{ marginBottom: 2 }}
            >
                Download Provident Fund Report (excel)
            </Button>
            
            <Button style={{marginLeft: '20px',marginBottom: '14px'}} variant="contained" color="secondary" onClick={downloadTxtFile} sx={{ color: 'white' }}>Generate ECR</Button>

            {(pfDetail?.rulesApplied || (pfDetail?.EffectiveFrom ? [pfDetail] : [])).map((rule) => (
                <Typography key={rule.EffectiveFrom} variant="body2" sx={{ color: 'text.secondary' }}>
                    PF rules applied for {month} {year}
                    {rule.AppliesFrom
                        ? ` (${formatDate(rule.AppliesFrom)} – ${formatDate(rule.AppliesTo)})`
                        : ''}
                    : effective from {formatDate(rule.EffectiveFrom)}
                    {' '}| PF limit ₹{Number(rule.PfBasicLimit || 0).toLocaleString('en-IN')} @ {rule.PfBasicPer ?? 0}%
                    {' '}| EPS limit ₹{Number(rule.epsSalaryLimit || 0).toLocaleString('en-IN')} @ {rule.epsPercent ?? 0}%
                    {' '}| EDLI limit ₹{Number(rule.edliSalaryLimit || 0).toLocaleString('en-IN')} @ {rule.edliPercent ?? 0}%
                </Typography>
            ))}
            {pfDetail?.rulesApplied?.length > 1 && (
                <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
                    PF rules changed during the month: employee PF, EPS and EDLI are split day-wise; admin and
                    inspection charges use the rules in force at month end.
                </Typography>
            )}
            
            <div
                style={{
                    height: '600px',
                    width: '100%',
                    maxWidth: '100%',
                    overflow: 'auto',
                    marginTop: '20px',
                    border: '1px solid #dee2e6',
                    borderRadius: '4px',
                    backgroundColor: '#fff'
                }}
            >
            <table 
                ref = { PFTableRef }
                style = {{
                    display: 'table',
                    borderCollapse: 'collapse',
                    width: '100%',
                    minWidth: extraCols ? `${1400 + extraCols * 120}px` : undefined,
                    tableLayout: 'fixed',
                    margin: 0
                }}
            >
                <colgroup>
                    <col style={{ width: '3.5%' }} />
                    <col style={{ width: '6%' }} />
                    <col style={{ width: '10%' }} />
                    <col style={{ width: '5%' }} />
                    <col style={{ width: '4%' }} />
                    <col style={{ width: '8%' }} />
                    <col style={{ width: '8%' }} />
                    <col style={{ width: '7.5%' }} />
                    <col style={{ width: '8%' }} />
                    <col style={{ width: '6.5%' }} />
                    {splitColumnHeaders.map((name) => <col key={name} style={{ width: '7%' }} />)}
                    <col style={{ width: '5.5%' }} />
                    <col style={{ width: '5.5%' }} />
                    <col style={{ width: '5.5%' }} />
                    <col style={{ width: '6%' }} />
                    <col style={{ width: '5.5%' }} />
                    <col style={{ width: '5.5%' }} />
                </colgroup>
                <thead style={{ position: 'sticky', top: 0, zIndex: 10, background: '#212529', color: 'white', boxShadow: '0 2px 2px -1px rgba(0,0,0,0.2)' }}>
                    <tr>
                        <th style={{textAlign: 'center', padding: '12px 8px', borderBottom: '1px solid #dee2e6'}}>Sr No</th>
                        <th style={{textAlign: 'left', padding: '12px 8px', borderBottom: '1px solid #dee2e6'}}>Emp Code</th>
                        <th style={{textAlign: 'left', padding: '12px 8px', borderBottom: '1px solid #dee2e6'}}>Employee Name</th>
                        <th style={{textAlign: 'right', padding: '12px 8px', borderBottom: '1px solid #dee2e6'}}>Total Days</th>
                        <th style={{textAlign: 'right', padding: '12px 8px', borderBottom: '1px solid #dee2e6'}}>NCP</th>
                        <th style={{textAlign: 'left', padding: '12px 8px', borderBottom: '1px solid #dee2e6'}}>UAN</th>
                        <th style={{textAlign: 'left', padding: '12px 8px', borderBottom: '1px solid #dee2e6'}}>PF No</th>
                        <th style={{textAlign: 'right', padding: '12px 10px', borderBottom: '1px solid #dee2e6', whiteSpace: 'nowrap'}}>Basic</th>
                        <th style={{textAlign: 'right', padding: '12px 10px', borderBottom: '1px solid #dee2e6', whiteSpace: 'nowrap'}}>Basic PF</th>
                        <th style={{textAlign: 'right', padding: '12px 8px', borderBottom: '1px solid #dee2e6'}}>Gross</th>
                        {splitColumnHeaders.map((name) => (
                            <th key={name} style={{textAlign: 'right', padding: '12px 8px', borderBottom: '1px solid #dee2e6', backgroundColor: '#5c4b00', whiteSpace: 'normal'}}>{name}</th>
                        ))}
                        <th style={{textAlign: 'right', padding: '12px 8px', borderBottom: '1px solid #dee2e6'}}>EPF</th>
                        <th style={{textAlign: 'right', padding: '12px 8px', borderBottom: '1px solid #dee2e6'}}>VPF</th>
                        <th style={{textAlign: 'right', padding: '12px 8px', borderBottom: '1px solid #dee2e6'}}>EPS</th>
                        <th style={{textAlign: 'right', padding: '12px 8px', borderBottom: '1px solid #dee2e6'}}>EPF-EPS</th>
                        <th style={{textAlign: 'right', padding: '12px 8px', borderBottom: '1px solid #dee2e6'}}>Total</th>
                        <th style={{textAlign: 'right', padding: '12px 8px', borderBottom: '1px solid #dee2e6'}}>EDLI</th>
                    </tr>
                </thead>
                <tbody>
                    {
                        empFilteredDetails.map((curElem)=>{
                            const { Basic_for_PF, EPF, EPS, EDLI } = curElem.PF;
                            const VPF = Math.round(Number(curElem.VPF) || 0);
                            const EPF_EPS = EPF - EPS;
                            const Total = EPS + EPF_EPS;

                            total_Basic        += Number(curElem.Basic)
                            total_Basic_for_PF += Basic_for_PF
                            total_Gross        += curElem.Gross
                            total_EPF          += EPF
                            total_VPF          += VPF
                            total_EPS          += EPS
                            total_EPF_EPS      += EPF_EPS
                            total_Total        += Total
                            total_EDLI         += EDLI
                            total_NCP_days     += curElem.NCP_days
                            curElem.Split.forEach((part, index) => {
                                splitTotals[index].grossShare += part.grossShare;
                                splitTotals[index].wages += part.wages;
                                splitTotals[index].epf += part.epf;
                            });
                            return (
                                <tr key = { curElem.Sr_No } style={{ backgroundColor: 'white' }}>
                                    <td style={{textAlign: 'center', padding: '12px 8px', borderBottom: '1px solid #dee2e6'}}>{curElem.Sr_No+1}</td>
                                    <td style={{textAlign: 'left', padding: '12px 8px', borderBottom: '1px solid #dee2e6'}}>{curElem.Employee_Code}</td>
                                    <td style={{textAlign: 'left', padding: '12px 8px', borderBottom: '1px solid #dee2e6'}}>{curElem.Employee_Name}</td>
                                    <td style={{textAlign: 'right', padding: '12px 8px', borderBottom: '1px solid #dee2e6'}}>{curElem.Total_no_of_days}</td>
                                    <td style={{textAlign: 'right', padding: '12px 8px', borderBottom: '1px solid #dee2e6'}}>{curElem.NCP_days}</td>
                                    <td style={{textAlign: 'left', padding: '12px 8px', borderBottom: '1px solid #dee2e6'}} data-uan="true">{cleanDisplayValue(curElem.UAN_Number)}</td>
                                    <td style={{textAlign: 'left', padding: '12px 8px', borderBottom: '1px solid #dee2e6'}} data-pf="true">{cleanDisplayValue(curElem.PF_number)}</td>
                                    <td style={{textAlign: 'right', padding: '12px 10px', borderBottom: '1px solid #dee2e6', whiteSpace: 'nowrap'}}>{formatNumber(curElem.Basic)}</td>
                                    <td style={{textAlign: 'right', padding: '12px 10px', borderBottom: '1px solid #dee2e6', whiteSpace: 'nowrap'}}>{formatNumber(Basic_for_PF)}</td>
                                    <td style={{textAlign: 'right', padding: '12px 8px', borderBottom: '1px solid #dee2e6'}}>{formatNumber(curElem.Gross)}</td>
                                    {curElem.Split.flatMap((part, index) => [
                                        <td key={`gross-${index}`} style={splitCellStyle}>{formatNumber(part.grossShare)}</td>,
                                        <td key={`cap-${index}`} style={splitCellStyle}>{formatNumber(part.cap)}</td>,
                                        <td key={`wages-${index}`} style={splitCellStyle}>{formatNumber(part.wages)}</td>,
                                        <td key={`epf-${index}`} style={splitCellStyle}>{formatNumber(part.epf)}</td>,
                                    ])}
                                    <td style={{textAlign: 'right', padding: '12px 8px', borderBottom: '1px solid #dee2e6'}}>{formatNumber(EPF)}</td>
                                    <td style={{textAlign: 'right', padding: '12px 8px', borderBottom: '1px solid #dee2e6'}}>{formatNumber(VPF)}</td>
                                    <td style={{textAlign: 'right', padding: '12px 8px', borderBottom: '1px solid #dee2e6'}}>{formatNumber(EPS)}</td>
                                    <td style={{textAlign: 'right', padding: '12px 8px', borderBottom: '1px solid #dee2e6'}}>{formatNumber(EPF_EPS)}</td>
                                    <td style={{textAlign: 'right', padding: '12px 8px', borderBottom: '1px solid #dee2e6'}}>{formatNumber(Total)}</td>
                                    <td style={{textAlign: 'right', padding: '12px 8px', borderBottom: '1px solid #dee2e6'}}>{formatNumber(EDLI)}</td>
                                </tr>
                            )
                        })
                    }

                    <tr style={{ backgroundColor: '#f8f9fa', fontWeight: 'bold' }}>
                        <td style={{textAlign: 'center', padding: '12px 8px', borderBottom: '1px solid #dee2e6'}}> </td>
                        <td style={{textAlign: 'left', padding: '12px 8px', borderBottom: '1px solid #dee2e6'}}> </td>
                        <td style={{textAlign: 'left', padding: '12px 8px', borderBottom: '1px solid #dee2e6'}}>GRAND TOTAL</td>
                        <td style={{textAlign: 'right', padding: '12px 8px', borderBottom: '1px solid #dee2e6'}}> </td>
                        <td style={{textAlign: 'right', padding: '12px 8px', borderBottom: '1px solid #dee2e6'}}>{Math.round(total_NCP_days)}</td>
                        <td style={{textAlign: 'left', padding: '12px 8px', borderBottom: '1px solid #dee2e6'}}> </td>
                        <td style={{textAlign: 'left', padding: '12px 8px', borderBottom: '1px solid #dee2e6'}}> </td>
                        <td style={{textAlign: 'right', padding: '12px 10px', borderBottom: '1px solid #dee2e6', whiteSpace: 'nowrap'}}>{formatNumber(total_Basic)}</td>
                        <td style={{textAlign: 'right', padding: '12px 10px', borderBottom: '1px solid #dee2e6', whiteSpace: 'nowrap'}}>{formatNumber(total_Basic_for_PF)}</td>
                        <td style={{textAlign: 'right', padding: '12px 8px', borderBottom: '1px solid #dee2e6'}}>{formatNumber(total_Gross)}</td>
                        {splitTotals.flatMap((total, index) => [
                            <td key={`gross-${index}`} style={splitCellStyle}>{formatNumber(total.grossShare)}</td>,
                            <td key={`cap-${index}`} style={splitCellStyle}> </td>,
                            <td key={`wages-${index}`} style={splitCellStyle}>{formatNumber(total.wages)}</td>,
                            <td key={`epf-${index}`} style={splitCellStyle}>{formatNumber(total.epf)}</td>,
                        ])}
                        <td style={{textAlign: 'right', padding: '12px 8px', borderBottom: '1px solid #dee2e6'}}>{formatNumber(total_EPF)}</td>
                        <td style={{textAlign: 'right', padding: '12px 8px', borderBottom: '1px solid #dee2e6'}}>{formatNumber(total_VPF)}</td>
                        <td style={{textAlign: 'right', padding: '12px 8px', borderBottom: '1px solid #dee2e6'}}>{formatNumber(total_EPS)}</td>
                        <td style={{textAlign: 'right', padding: '12px 8px', borderBottom: '1px solid #dee2e6'}}>{formatNumber(total_EPF_EPS)}</td>
                        <td style={{textAlign: 'right', padding: '12px 8px', borderBottom: '1px solid #dee2e6'}}>{formatNumber(total_Total)}</td>
                        <td style={{textAlign: 'right', padding: '12px 8px', borderBottom: '1px solid #dee2e6'}}>{formatNumber(total_EDLI)}</td>
                    </tr>

                    <tr>
                        <td colSpan={2} style={{textAlign: 'left', border: '1px solid #999', padding: '8px'}}>&nbsp;</td>
                        <td colSpan={2} style={{textAlign: 'left', border: '1px solid #999', padding: '8px'}}> </td>
                    </tr>

                    {/* Summary Rows in New Order */}
                    <tr>
                        <th colSpan={2} style={{textAlign: 'left', border: '1px solid #999', padding: '8px', whiteSpace: 'normal', wordWrap: 'break-word', maxWidth: '220px'}}>Total number of employees</th>
                        <td colSpan={2} style={{textAlign: 'right', border: '1px solid #999', padding: '8px'}}>{totalEmployees}</td>
                    </tr>

                    <tr>
                        <th colSpan={2} style={{textAlign: 'left', border: '1px solid #999', padding: '8px', whiteSpace: 'normal', wordWrap: 'break-word', maxWidth: '220px'}}>Total Subscibers</th>
                        <td colSpan={2} style={{textAlign: 'right', border: '1px solid #999', padding: '8px'}}>{empFilteredDetails.length}</td>
                    </tr>

                    <tr>
                        <th colSpan={2} style={{textAlign: 'left', border: '1px solid #999', padding: '8px', whiteSpace: 'normal', wordWrap: 'break-word', maxWidth: '220px'}}>Total number of employees excluded</th>
                        <td colSpan={2} style={{textAlign: 'right', border: '1px solid #999', padding: '8px'}}>{totalExcludedEmployees}</td>
                    </tr>

                    <tr>
                        <th colSpan={2} style={{textAlign: 'left', border: '1px solid #999', padding: '8px', whiteSpace: 'normal', wordWrap: 'break-word', maxWidth: '220px'}}>Gross salary of excluded emp</th>
                        <td colSpan={2} style={{textAlign: 'right', border: '1px solid #999', padding: '8px'}}>{formatNumber(totalExcludedEmployeesGross)}</td>
                    </tr>

                    <tr>
                        <th colSpan={2} style={{textAlign: 'left', border: '1px solid #999', padding: '8px', whiteSpace: 'normal', wordWrap: 'break-word', maxWidth: '220px'}}>Basic salary of the excluded employee</th>
                        <td colSpan={2} style={{textAlign: 'right', border: '1px solid #999', padding: '8px'}}>{formatNumber(totalExcludedEmployeesBasic)}</td>
                    </tr>

                    <tr>
                        <th colSpan={2} style={{textAlign: 'left', border: '1px solid #999', padding: '8px', whiteSpace: 'normal', wordWrap: 'break-word', maxWidth: '220px'}}>Total NCP days</th>
                        <td colSpan={2} style={{textAlign: 'right', border: '1px solid #999', padding: '8px'}}>{Math.round(total_NCP_days)}</td>
                    </tr>
                    
                    <tr>
                        <td colSpan={16 + extraCols} style={{textAlign: 'left', border: '1px solid #999', padding: '8px'}}>&nbsp;</td>
                    </tr>

                    {/* Account Wise Bifurcation Header */}
                    <tr>
                        <th colSpan={16 + extraCols} style={{textAlign: 'center', border: '1px solid #999', padding: '12px', fontWeight: 'bold', fontSize: '16px', backgroundColor: '#f0f0f0'}}>
                            Account Wise Bifurcation of Balance Amount
                        </th>
                    </tr>

                    {/* Total EPF Contribution (A/C 1) — includes VPF in EE share */}
                    <tr>
                        <th colSpan={5} style={{textAlign: 'left', border: '1px solid #999', padding: '8px', whiteSpace: 'normal', wordWrap: 'break-word'}}>
                            Total EPF Contribution (A/C 1)
                        </th>
                        <td colSpan={8 + extraCols} style={{textAlign: 'left', border: '1px solid #999', padding: '8px', whiteSpace: 'normal', wordWrap: 'break-word'}}>
                            Total EPF Contribution EE Share (incl. VPF) + Total EPF Contribution ER Share + Total Refund of Advance (₹)
                        </td>
                        <td colSpan={3} style={{textAlign: 'right', border: '1px solid #999', padding: '8px', fontWeight: 'bold'}}>
                            {formatNumber(total_EPF + total_VPF + total_EPF_EPS)}
                        </td>
                    </tr>

                    {/* Total EPF Charges (A/C 2) */}
                    <tr>
                        <th rowSpan={2} colSpan={5} style={{textAlign: 'left', border: '1px solid #999', padding: '8px', whiteSpace: 'normal', wordWrap: 'break-word', verticalAlign: 'top'}}>
                            Total EPF Charges (A/C 2)
                        </th>
                        <td colSpan={8 + extraCols} style={{textAlign: 'left', border: '1px solid #999', padding: '8px'}}>
                            Administration Charge (₹)
                        </td>
                        <td colSpan={3} style={{textAlign: 'right', border: '1px solid #999', padding: '8px'}}>
                            {formatNumber(total_Basic_for_PF*epfAdminPercent<=epfAdminMinAmt?epfAdminMinAmt:total_Basic_for_PF*epfAdminPercent)}
                        </td>
                    </tr>
                    <tr>
                        <td colSpan={8 + extraCols} style={{textAlign: 'left', border: '1px solid #999', padding: '8px'}}>
                            Inspection Charge (₹)
                        </td>
                        <td colSpan={3} style={{textAlign: 'right', border: '1px solid #999', padding: '8px'}}>
                            Not Applicable
                        </td>
                    </tr>

                    {/* Total EPS Contribution (A/C 10) */}
                    <tr>
                        <th colSpan={5} style={{textAlign: 'left', border: '1px solid #999', padding: '8px', whiteSpace: 'normal', wordWrap: 'break-word'}}>
                            Total EPS Contribution (A/C 10) (₹)
                        </th>
                        <td colSpan={8 + extraCols} style={{textAlign: 'left', border: '1px solid #999', padding: '8px'}}>
                            &nbsp;
                        </td>
                        <td colSpan={3} style={{textAlign: 'right', border: '1px solid #999', padding: '8px', fontWeight: 'bold'}}>
                            {formatNumber(total_EPS)}
                        </td>
                    </tr>

                    {/* Total EDLI Contribution (ER Share A/C 21) */}
                    <tr>
                        <th colSpan={5} style={{textAlign: 'left', border: '1px solid #999', padding: '8px', whiteSpace: 'normal', wordWrap: 'break-word'}}>
                            Total EDLI Contribution (ER Share A/C 21) (₹)
                        </th>
                        <td colSpan={8 + extraCols} style={{textAlign: 'left', border: '1px solid #999', padding: '8px'}}>
                            &nbsp;
                        </td>
                        <td colSpan={3} style={{textAlign: 'right', border: '1px solid #999', padding: '8px', fontWeight: 'bold'}}>
                            {formatNumber(total_EDLI)}
                        </td>
                    </tr>

                    {/* Total EDLI Charges (A/C 22) */}
                    <tr>
                        <th rowSpan={2} colSpan={5} style={{textAlign: 'left', border: '1px solid #999', padding: '8px', whiteSpace: 'normal', wordWrap: 'break-word', verticalAlign: 'top'}}>
                            Total EDLI Charges (A/C 22) (₹)
                        </th>
                        <td colSpan={8 + extraCols} style={{textAlign: 'left', border: '1px solid #999', padding: '8px'}}>
                            Administration Charges (₹)
                        </td>
                        <td colSpan={3} style={{textAlign: 'right', border: '1px solid #999', padding: '8px'}}>
                            0
                        </td>
                    </tr>
                    <tr>
                        <td colSpan={8 + extraCols} style={{textAlign: 'left', border: '1px solid #999', padding: '8px'}}>
                            Inspection Charge (₹)
                        </td>
                        <td colSpan={3} style={{textAlign: 'right', border: '1px solid #999', padding: '8px'}}>
                            Not Applicable
                        </td>
                    </tr>

                    {/* Total Amount */}
                    <tr>
                        <th colSpan={13 + extraCols} style={{textAlign: 'right', border: '1px solid #999', padding: '12px', fontWeight: 'bold', backgroundColor: '#d4edda', fontSize: '14px'}}>
                            Total Amount (₹)
                        </th>
                        <th colSpan={3} style={{textAlign: 'right', border: '1px solid #999', padding: '12px', fontWeight: 'bold', backgroundColor: '#d4edda', fontSize: '14px'}}>
                            {formatNumber(
                                total_EPF
                                + total_VPF
                                + total_EPF_EPS 
                                + total_EPS 
                                + Math.round(total_Basic_for_PF*epfAdminPercent<=epfAdminMinAmt?epfAdminMinAmt:total_Basic_for_PF*epfAdminPercent) 
                                + total_EDLI 
                                + Math.round(total_Basic_for_PF*epfInspectionPercent<=epfInspectionMinAmt?epfInspectionMinAmt:total_Basic_for_PF*epfInspectionPercent) 
                                + 0 
                                + Math.round(total_Basic_for_PF*edliInspectionPercent<=edliInspectionMinAmt?edliInspectionMinAmt:total_Basic_for_PF*edliInspectionPercent)
                            )}
                        </th>
                    </tr>
                </tbody>
            </table>
            </div>
            </Box>
        </Box>
    )
}

export default PFReport;